import { type ReactNode, useCallback, useEffect, useState } from 'react'

import { useTranslate } from '../../hooks/useTranslate'
import { ConfirmationBox } from '../../structure/confirm/ConfirmationBox'
import { Provider } from './unsavedChangesContext'

const alertUser = (e: BeforeUnloadEvent) => {
  e.preventDefault()
  e.returnValue = '' // deprecated, but taken as-is from an older project
}

export type ProviderPropsInternal = {
  isDirty: boolean
  isSubmitting: boolean
  isOpen: boolean
  onClose: () => void
  children:
    | ReactNode
    | ((currentContext: Pick<ProviderPropsInternal, 'onClose'>) => ReactNode)
}

export type ProviderPropsExternal = {
  onIsDirtyChanged: (isDirty: boolean) => void
  onIsSubmittingChanged: (isSubmitting: boolean) => void
}

/** Context Provider used mainly for portals to manage dirty form states.  */
const UnsavedChangesProvider = ({
  children,
  isDirty,
  isOpen,
  isSubmitting,
  onClose,
}: ProviderPropsInternal) => {
  const translate = useTranslate()
  const [isConfirmingClose, setIsConfirmingClose] = useState(false)
  const [isDirtyInternal, setIsDirtyInternal] = useState(isDirty)
  const [isSubmittingInternal, setIsSubmittingInternal] = useState(isSubmitting)

  const handleOnClose = useCallback(() => {
    if (isDirtyInternal) {
      setIsConfirmingClose(true)
    } else {
      onClose()
    }
  }, [isDirtyInternal, onClose])

  useEffect(() => {
    if (isOpen && (isDirtyInternal || isSubmittingInternal)) {
      window.addEventListener('beforeunload', alertUser)
      return () => {
        window.removeEventListener('beforeunload', alertUser)
      }
    }
    return undefined
  }, [isDirtyInternal, isOpen, isSubmittingInternal])

  const value = {
    isDirty: isDirtyInternal,
    isSubmitting: isSubmittingInternal,
    isOpen,
    onClose: handleOnClose,
    onIsDirtyChanged: setIsDirtyInternal,
    onIsSubmittingChanged: setIsSubmittingInternal,
  }

  return (
    <Provider value={value}>
      <>
        {isConfirmingClose && (
          <ConfirmationBox
            isOpen
            title={translate('confirmClose.title')}
            confirmLabel={translate('confirmClose.accept')}
            onCancel={() => setIsConfirmingClose(false)}
            onAccept={() => {
              setIsConfirmingClose(false)
              onClose()
            }}
          >
            {translate('confirmClose.body')}
          </ConfirmationBox>
        )}
      </>
      {typeof children === 'function' ? children(value) : children}
    </Provider>
  )
}

export { UnsavedChangesProvider }
